"use client";

import { useState } from "react";
import { Check, Funnel as Filter } from 'lucide-react';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const filters = [{
  value: "all",
  label: "All"
}, {
  value: "campaign",
  label: "Campaign"
}, {
  value: "video",
  label: "Video"
}, {
  value: "branding",
  label: "Branding"
}, {
  value: "social",
  label: "Social"
}];
const projects = [{
  id: 1,
  name: "Neon Pulse Launch",
  neighborhood: "Global",
  date: "Q1 2024",
  category: "campaign",
  categoryLabel: "Campaign",
  extraTag: "Strategy",
  height: "h-[320px] md:h-[420px]",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/neon_pulse_concept.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/neon_pulse_final.png",
  coords: "47.6685°N · 122.3838°W"
}, {
  id: 2,
  name: "Velocity Motion Spot",
  neighborhood: "Studio",
  date: "Q1 2024",
  category: "video",
  categoryLabel: "Video",
  extraTag: "Cinematic",
  height: "h-[400px] md:h-[540px]",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/velocity_storyboard.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/velocity_final_spot.webp",
  coords: "47.6253°N · 122.3222°W"
}, {
  id: 3,
  name: "Identity Rebrand",
  neighborhood: "Digital",
  date: "Q4 2023",
  category: "branding",
  categoryLabel: "Branding",
  extraTag: "Visual",
  height: "h-[360px] md:h-[460px]",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/brand_moodboard.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/brand_final_assets.webp",
  coords: "47.6370°N · 122.3565°W"
}, {
  id: 4,
  name: "Social First Series",
  neighborhood: "Mobile",
  date: "Q1 2024",
  category: "social",
  categoryLabel: "Social",
  extraTag: "Viral",
  height: "h-[400px] md:h-[520px]",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/social_drafts.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/social_final_cut.png",
  coords: "47.6510°N · 122.3500°W"
}, {
  id: 5,
  name: "Tech Horizon Film",
  neighborhood: "Production",
  date: "Q4 2023",
  category: "video",
  categoryLabel: "Video",
  extraTag: "High-End",
  height: "h-[320px] md:h-[400px]",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/tech_script.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/tech_final_film.png",
  coords: "47.6128°N · 122.2879°W"
}, {
  id: 6,
  name: "Global Impact Drive",
  neighborhood: "Integrated",
  date: "Q3 2023",
  category: "campaign",
  categoryLabel: "Campaign",
  extraTag: "Scale",
  height: "h-[440px] md:h-[560px]",
  beforeImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/impact_strategy.png",
  afterImg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/impact_execution.webp",
  coords: "47.6614°N · 122.3344°W"
}];
export default function ThePortfolio() {
  const [activeFilter, setActiveFilter] = useState("all");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const filteredProjects = activeFilter === "all" ? projects : projects.filter(p => p.category === activeFilter);
  return <section data-section-id="1733" id="the-portfolio" className="relative overflow-hidden bg-background py-32 md:py-44 font-default">
      <svg aria-hidden="true" className="pointer-events-none absolute left-0 top-32 hidden lg:block opacity-60" width="80" height="320" viewBox="0 0 80 320" fill="none">
        <path d="M40 10 Q35 60 40 110 Q45 160 40 210 Q35 260 40 310" stroke="var(--primary)" strokeWidth="1" fill="none" />
        <path d="M40 40 Q20 35 12 50 M40 40 Q60 35 68 50" stroke="var(--primary)" strokeWidth="1" fill="none" />
        <path d="M40 80 Q18 78 8 96 M40 80 Q62 78 72 96" stroke="var(--primary)" strokeWidth="1" fill="none" />
        <path d="M40 130 Q22 128 14 144 M40 130 Q58 128 66 144" stroke="var(--primary)" strokeWidth="1" fill="none" />
        <path d="M40 180 Q18 178 10 196 M40 180 Q62 178 70 196" stroke="var(--primary)" strokeWidth="1" fill="none" />
        <path d="M40 230 Q24 228 16 244 M40 230 Q56 228 64 244" stroke="var(--primary)" strokeWidth="1" fill="none" />
        <path d="M40 280 Q20 278 12 294 M40 280 Q60 278 68 294" stroke="var(--primary)" strokeWidth="1" fill="none" />
      </svg>

      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "radial-gradient(circle at 20% 30%, var(--foreground) 1px, transparent 1px), radial-gradient(circle at 70% 70%, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "32px 32px, 48px 48px"
    }} />

      <div className="container mx-auto max-w-[1280px] px-6 md:px-12 relative">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end mb-16 md:mb-24 transition-all duration-[620ms] ease-[cubic-bezier(0.65,0,0.35,1)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.14em] text-primary mb-6" style={{
            letterSpacing: "0.14em"
          }}><span className="text-[1.4em] mr-1" data-ast-id="f:/src/pages/OurWork/sections/ThePortfolio.tsx|sig:6x5iz" data-ast-start="6253" data-ast-end="6297">§</span>Vivid Studio&nbsp;</p>
            <h2 className="font-default text-foreground leading-[1.02] tracking-tight" style={{
            fontSize: "clamp(2.25rem, 4.4vw, 4.25rem)",
            fontWeight: 700
          }}>
              Campaigns that{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-primary">resonate</span>
                <svg aria-hidden="true" viewBox="0 0 220 40" className="absolute -bottom-2 left-0 w-full h-[0.4em] z-0" preserveAspectRatio="none">
                  <path d="M5 28 Q40 12 90 22 Q140 32 180 18 Q200 12 215 24" stroke="var(--primary)" strokeWidth="3" fill="none" strokeLinecap="round" style={{
                  strokeDasharray: 240,
                  strokeDashoffset: headerVisible ? 0 : 240,
                  transition: "stroke-dashoffset 720ms cubic-bezier(0.65,0,0.35,1) 200ms"
                }} />
                </svg>
              </span>
              .
            </h2>
            <p className="mt-6 max-w-xl text-muted-foreground text-base md:text-lg leading-relaxed">
              From initial concept mood boards to high-end commercial video spots, we build end-to-end creative campaigns that define brands.
            </p>
          </div>

          <div className="lg:col-span-5 hidden lg:flex lg:justify-end">
            <div className="flex flex-col gap-3 items-end">
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                Filter the work
              </p>
              <ToggleGroup type="single" value={activeFilter} onValueChange={v => v && setActiveFilter(v)} className="flex flex-wrap gap-2 justify-end bg-transparent">
                {filters.map((f, i) => <ToggleGroupItem key={f.value} data-index={i} value={f.value} className="rounded-lg border border-border bg-card text-foreground px-4 py-1.5 text-xs font-medium hover:bg-accent data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary transition-all duration-220 first:rounded-l-lg last:rounded-r-lg">
                    {activeFilter === f.value && <Check className="size-3 mr-1" />}
                    {f.label}
                  </ToggleGroupItem>)}
              </ToggleGroup>
            </div>
          </div>

          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="rounded-lg border-border text-foreground bg-transparent hover:bg-accent">
                  <Filter className="size-4" />
                  Filter — {filters.find(f => f.value === activeFilter)?.label}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="bg-background border-border">
                <SheetHeader>
                  <SheetTitle className="font-default text-2xl text-foreground">Filter work</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-2 p-4">
                  {filters.map((f, i) => <Button key={f.value} data-index={i} variant={activeFilter === f.value ? "default" : "outline"} onClick={() => setActiveFilter(f.value)} className={`justify-start rounded-lg ${activeFilter === f.value ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border-border text-foreground bg-transparent"}`}>
                      {activeFilter === f.value && <Check className="size-4" />}
                      {f.label}
                    </Button>)}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-12 md:mb-16">
          <div className="flex-1 h-px" style={{
          backgroundImage: "repeating-linear-gradient(to right, var(--primary) 0, var(--primary) 4px, transparent 4px, transparent 8px)"
        }} />
          <span className="size-1.5 rounded-lg bg-primary" />
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredProjects.map((project, i) => <article key={project.id} data-index={i} className={`group relative bg-card rounded-lg border border-border p-5 transition-all duration-[280ms] ease-out hover:-translate-y-[3px] hover:border-primary hover:shadow-md ${i % 3 === 1 ? "lg:mt-12" : ""} ${i % 3 === 2 ? "lg:mt-6" : ""} ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: gridVisible ? `${i * 80}ms` : "0ms",
          transitionProperty: "opacity, transform, border-color, box-shadow"
        }}>
              <div className="absolute top-3 right-3 z-10 transition-transform duration-[280ms] group-hover:rotate-[2deg]" style={{
            transform: "rotate(6deg)"
          }}>
                <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                  {project.coords}
                </p>
                <p className="text-primary text-right -mt-0.5" style={{
              fontFamily: "monospace",
              fontSize: "0.95rem",
              lineHeight: 1
            }}>
                   vivid
                </p>
              </div>

              <div className={`relative overflow-hidden rounded-md ${project.height}`}>
                <AspectRatio ratio={4 / 5} className="h-full">
                  <div className="absolute inset-0 flex h-full w-full">
                    <div className="relative h-full w-1/2 overflow-hidden transition-all duration-[280ms] group-hover:w-[42%]">
                      <img src={project.beforeImg} alt={`${project.name} concept`} loading="lazy" decoding="async" className="h-full w-full object-cover" style={{
                    filter: "grayscale(0.7) sepia(0.2) hue-rotate(50deg) brightness(0.95)"
                  }} />
                      <div className="absolute inset-0 mix-blend-multiply" style={{
                    backgroundColor: "var(--primary)",
                    opacity: 0.25
                  }} />
                      <span className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-wider text-background bg-foreground/80 px-1.5 py-0.5 rounded">
                        concept
                      </span>
                    </div>

                    <div className="relative w-px bg-transparent flex items-center justify-center">
                      <svg aria-hidden="true" className="absolute inset-y-0 left-1/2 -translate-x-1/2 h-full" width="2" viewBox="0 0 2 400" preserveAspectRatio="none">
                        <path d="M1 0 Q1.5 100 1 200 Q0.5 300 1 400" stroke="var(--primary)" strokeWidth="1.5" fill="none" />
                      </svg>
                      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-90 whitespace-nowrap text-primary px-1 bg-card" style={{
                    fontFamily: "monospace",
                    fontSize: "1rem",
                    lineHeight: 1
                  }}>
                        final →
                      </span>
                    </div>

                    <div className="relative h-full flex-1 overflow-hidden">
                      <img src={project.afterImg} alt={`${project.name} final`} loading="lazy" decoding="async" className="h-full w-full object-cover" style={{
                    filter: "saturate(0.9) brightness(1.02)"
                  }} />
                    </div>
                  </div>
                </AspectRatio>
              </div>

              <div className="mt-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">
                  {project.neighborhood} · {project.date}
                </p>
                <h3 className="font-default text-foreground" style={{
              fontSize: "clamp(1.25rem, 1.6vw, 1.625rem)",
              fontWeight: 600,
              lineHeight: 1.15
            }}>
                  {project.name}
                </h3>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="rounded-lg border-primary text-primary bg-transparent text-[10px] uppercase tracking-wider font-medium">
                    {project.categoryLabel}
                  </Badge>
                  <Badge variant="outline" className="rounded-lg border-primary text-primary bg-transparent text-[10px] uppercase tracking-wider font-medium">
                    {project.extraTag}
                  </Badge>
                </div>
              </div>
            </article>)}
        </div>

        {filteredProjects.length === 0 && <div className="text-center py-20">
            <p className="font-default text-2xl text-foreground mb-2">No projects found.</p>
            <p className="text-muted-foreground">Try another filter to see our creative work.</p>
          </div>}
      </div>
    </section>;
}